import React, { useCallback, useEffect, useState, useMemo, useRef, useContext, ReactNode } from "react";
import { BeforeDragStartEvent, DragDropProvider, DragOverlay, useDroppable } from "@dnd-kit/react";
import { DeliveryComponent, CurrentTimeContext, DraggableDeliveryComponent } from "./delivery";

import { HeatMap } from "./heatmap";
import { Dashboard, Driver, Delivery, DeliveryGroup, IDispatchClient } from "./model";
import { classNames } from "src/common_util";
import { DashboardConfig, DashboardContext, useDashboardConfig } from "./dispatch_api_client";
import { GroupComponent } from "./group";
import { DriverComponent } from "./driver";

interface DispatchDashboardProps {
  apiClient: IDispatchClient;
  currentAdminId: number;
  fixedTime?: Date;
}

interface TabNotification {
  order: number;
  driver: boolean;
}

interface DragData {
  type: string;
  id: string;
  unsetErrorTimer: () => void;
  handleDropping: () => void;
  handleDragFailure?: () => void;
}

const defaultTabNotification = {
  order: 0,
  driver: false
}

const DispatchDashboard = ({ apiClient, currentAdminId, fixedTime } : DispatchDashboardProps) => {
  const [ dashboard, setDashboard ] = useState(Dashboard.emptyDashboard());
  const [ connected, setConnected ] = useState(false);
  const [ now, setNow ] = useState(fixedTime ?? new Date());
  const [ busySection, setBusySection ] = useState(false);
  const [ draggedNode, setDraggedNode ] = useState<{ type: string; id: number | string } | null>(null);

  const originalDocumentTitle = useRef(document.title);
  const [ previousOrderNotificationAt, setPreviousOrderNotificationAt ] = useState(0);
  const [ previousDriverNotificationAt, setPreviousDriverNotificationAt ] = useState(0);
  const [ tabNotification, setTabNotification ] = useState<TabNotification>(defaultTabNotification);

  useEffect(() => {
    const deliverySorter = (a: Delivery, b: Delivery) : number => {
      // Sort order is pickupTime, pickupZoneId, storeName, suburbName, id
      if (a.pickupTimeIso8601 < b.pickupTimeIso8601)
        return -1;
      else if (a.pickupTimeIso8601 > b.pickupTimeIso8601)
        return 1;
      else if (a.storeName < b.storeName)
        return -1;
      else if (a.storeName > b.storeName)
        return 1;
      else if (a.suburb < b.suburb)
        return -1;
      else if (a.suburb > b.suburb)
        return 1;
      else if (a.id < b.id)
        return -1;
      else if (a.id > b.id)
        return 1;
      else
        return 0;
    }

    const handleDashboardUpdated = (newDashboard: Dashboard) => {
      newDashboard.deliveries.sort(deliverySorter);

      setDashboard(newDashboard);
    }

    const handleDashboardConnected = (connected: boolean) => {
      setConnected(connected);
    }

    const resetDocumentTitle = () => {
      setTabNotification(defaultTabNotification);
    }

    const intervalTimer = window.setInterval(() => {
      if (fixedTime) {
        return;
      }

      setNow(new Date());
    }, 5000);

    apiClient.dashboardUpdated.addListener(handleDashboardUpdated);
    apiClient.dashboardConnected.addListener(handleDashboardConnected);

    if (apiClient.dashboard) handleDashboardUpdated(apiClient.dashboard);
    setConnected(apiClient.connected);

    window.addEventListener("focus", resetDocumentTitle);

    return () => {
      clearInterval(intervalTimer);

      window.removeEventListener("focus", resetDocumentTitle);

      apiClient.dashboardConnected.removeListener(handleDashboardConnected);
      apiClient.dashboardUpdated.removeListener(handleDashboardUpdated);
    };
  }, [ apiClient, fixedTime ]);

  const descriptiveDriverList = (title : string, drivers: Driver[], showBusySectionToggle = false) : React.JSX.Element => {
    if (drivers.length == 0) {
      return <></>;
    }

    const handleBusySectionToggle = () => {
      setBusySection(!busySection);
    }

    let busySectionToggle = <></>;
    // if both busy sections are visible & it's the first busy section
    if (showBusySectionToggle && ((busySection && title == "Busy (picking up)") || (!busySection && title == "Busy (dropping off)"))) {
      busySectionToggle = <button className="button is-small is-text mr-2 has-tooltip-top has-tooltip-arrow" data-tooltip="Toggle busy sections" onClick={handleBusySectionToggle}>
        <span className="icon is-small">
          <i className="fa-solid fa-arrow-up-arrow-down"></i>
        </span>
      </button>;
    }

    return <>
      <div className="subheading">
        {busySectionToggle}
        <span className="text">{title}</span>
        <span className="count">{drivers.length}</span>
      </div>

      <DriverList
        dashboard={dashboard}
        drivers={drivers}
      />
    </>;
  }

  const hasCurfewHighlight = useMemo(() => {
    const hour = now.getHours();
    if (hour > 20 || hour < 5 || (hour == 20 && now.getMinutes() >= 45)) {
      return true;
    }
    return false;
  }, [now]);


  const handleDragStart = (event: { operation: { source: { data?: unknown } | null } }) => {
    const dragTarget = event.operation.source?.data as DragData | undefined;

    if (!dragTarget) return;

    dragTarget.unsetErrorTimer();
    setDraggedNode({ type: dragTarget.type, id: dragTarget.id });
  }

  const handleDragEnd = useCallback(async (event: { operation: { source: { data?: unknown } | null; target: { data?: unknown } | null }; canceled?: boolean }) => {
    setDraggedNode(null);

    // If drag was canceled, don't process the drop
    if (event.canceled) return;

    if (event.operation.target === null || !document.hasFocus()) return;

    const dragTarget = event.operation.source?.data as DragData | undefined;
    const dropTarget = event.operation.target?.data as DragData | undefined;

    if (!dragTarget || !dropTarget) return;

    if (dragTarget.type == "delivery") {
      if (dropTarget.type == "group") {
        dragTarget.handleDropping();
        const delivery = dashboard.deliveries.find(d => d.id == dragTarget.id);
        if (delivery) {
          await apiClient.allocateDeliveryToGroup(delivery.id, Number(dropTarget.id), delivery.driverId);
        }
      }
      else if (dropTarget.type == "driver") {
        dragTarget.handleDropping();
        await dashboard.handleAllocateDeliveryToDriver(dragTarget.id, Number(dropTarget.id), apiClient)
          .then(result => {
            if (result === false && dragTarget.handleDragFailure) {
              dragTarget.handleDragFailure();
            }
          });
      }
    }

    else if (dragTarget.type == "group") {
      if (dropTarget.type == "group") {
        // Because the group's own container is a valid droppable, & merging a group to itself is a valid action
        // specifically check & ignore that case (there is also some CSS to prevent high
        // lighting the container in this case)
        if (dragTarget.id == dropTarget.id) return;

        dragTarget.handleDropping();
        await apiClient.mergeGroups(Number(dragTarget.id), Number(dropTarget.id));
      }
      else if (dropTarget.type == "driver") {
        dragTarget.handleDropping();
        await dashboard.handleAllocateGroupToDriver(Number(dragTarget.id), Number(dropTarget.id), apiClient)
          .then(result => {
            if (result === false && dragTarget.handleDragFailure) {
              dragTarget.handleDragFailure();
            }
          });
      }
    }
  }, [ dashboard, apiClient ]);

  const draggedNodeClone = useMemo(() => {
    if (draggedNode?.type == "delivery") {
      const delivery = dashboard.deliveries.find(d => d.id == draggedNode.id);

      if (delivery) {
        return <div className="card is-card-dashboard">
          <div className="card-content p-0">
            <DeliveryComponent
              delivery={delivery}
              defaultExpand={true}
              deliveringNow={false}
              dashboard={dashboard}
            />
          </div>
        </div>;
      }
    }

    else if (draggedNode?.type == "group") {
      const group = dashboard.groups.find(g => g.id == draggedNode.id);
      const deliveries = dashboard.deliveriesForGroup(draggedNode.id as number);

      if (group && deliveries.length > 0) {
        return <GroupComponent
          group={group}
          deliveries={deliveries}
          dashboard={dashboard}
        />;
      }
    }

    return null;
  }, [draggedNode, dashboard]);

  const handleBeforeDragStart = useCallback((event: BeforeDragStartEvent) => {
    if (event.operation.activatorEvent
      && 'ctrlKey' in event.operation.activatorEvent
      && event.operation.activatorEvent.ctrlKey) {
      event.preventDefault();
    }
  }, []);

  const deliveryGroupList = <DeliveryGroupList
    dashboard={dashboard}
    deliveriesByGroup={dashboard.deliveriesByGroup()}
    apiClient={apiClient}
  />;

  const busyPickingUp = dashboard.driversBusyPickingUp();
  const busyDroppingOff = dashboard.driversBusyDroppingOff();
  const showBusySectionToggle = !!(busyPickingUp.length && busyDroppingOff.length);

  const driverCount = dashboard.driversActive().length + dashboard.driversRostered().length + dashboard.driversRosteredToStartSoon().length;
  const driversAwaitingDeliveriesArr = dashboard.driversAwaitingDeliveries();
  const driversAwaitingDeliveries = descriptiveDriverList("Waiting for orders", driversAwaitingDeliveriesArr);
  const driversIdle = descriptiveDriverList("Idle", dashboard.driversIdle());
  const driversBusyPickingUp = descriptiveDriverList("Busy (picking up)", busyPickingUp, showBusySectionToggle);
  const driversBusyDroppingOff = descriptiveDriverList("Busy (dropping off)", busyDroppingOff, showBusySectionToggle);
  const driversBusyFirst = busySection ? driversBusyPickingUp : driversBusyDroppingOff;
  const driversBusySecond = !busySection ? driversBusyPickingUp : driversBusyDroppingOff;
  const driversRostered = descriptiveDriverList("Rostered but not active", dashboard.driversRostered());
  const driversRosteredToStartSoon = descriptiveDriverList("Rostered to start soon", dashboard.driversRosteredToStartSoon());

  const unallocatedCurrent = dashboard.unallocatedCurrent(now);
  const unallocatedFuture = dashboard.unallocatedFuture(now);


  useEffect(() => {
    setPreviousOrderNotificationAt(unallocatedCurrent.length);

    if (document.hasFocus()) return;

    // Update tab title
    //  on any change to the incoming order count
    if (unallocatedCurrent.length != previousOrderNotificationAt)
      setTabNotification(prevState => ( { ...prevState, order: unallocatedCurrent.length } ));

    // Play a sound
    if (unallocatedCurrent.length > previousOrderNotificationAt) {
      const soundEvent = new CustomEvent("soundEvent");
      window.dispatchEvent(soundEvent);
    }
  }, [unallocatedCurrent.length, previousOrderNotificationAt]);

  useEffect(() => {
    setPreviousDriverNotificationAt(driversAwaitingDeliveriesArr.length);

    if (document.hasFocus()) return;

    // Update tab title
    //  when the number of drivers awaiting deliveries goes from 0 -> 1+
    if (previousDriverNotificationAt == 0 && driversAwaitingDeliveriesArr.length > 0)
      setTabNotification(prevState => ( { ...prevState, driver: true } ));
  }, [driversAwaitingDeliveriesArr.length, previousDriverNotificationAt]);

  // Handle updating the tab title, with multiple independent notifications
  useEffect(() => {
    const driverNotification = tabNotification.driver ? "! " : "";
    const orderNotification = tabNotification.order > 0 ? `(${tabNotification.order}) ` : "";
    document.title = driverNotification + orderNotification + originalDocumentTitle.current;
  }, [tabNotification]);

  return (
    <DashboardContext.Provider value={new DashboardConfig(apiClient)}>
      <CurrentTimeContext.Provider value={now}>
        <DragDropProvider
          onBeforeDragStart={handleBeforeDragStart}
          onDragStart={handleDragStart}
          onDragEnd={(event) => void handleDragEnd(event)}
        >
          <div className={"dispatch-dashboard is-size-7 " + (connected ? '' : 'is-disconnected') + (hasCurfewHighlight ? " highlight-restricted" : "")} >
            <DragOverlay className="drag-overlay" style={{ zIndex: 99 }}>{draggedNodeClone}</DragOverlay>
            <div className="dispatch-dashboard-fixed">
              <HeatMap slots={dashboard.heatMap(now)} />
            </div>

            <StatusBar dashboard={dashboard} currentAdminId={currentAdminId} />

            <div className="dispatch-dashboard-body">
              <div className="dispatch-dashboard-columns">
                <div className="dispatch-dashboard-section dispatch-dashboard-incoming-orders">
                  <div className="dispatch-dashboard-panel">
                    <div className="heading">
                      <span className="text">Incoming orders</span>
                      <span className="count">{unallocatedCurrent.length}</span>
                    </div>

                    <UnallocatedDeliveryList
                      unallocatedDeliveries={unallocatedCurrent}
                      dashboard={dashboard}
                    />

                    {unallocatedFuture.length > 0 ?
                      <>
                        <div className="heading">
                          <span className="text">Future orders</span>
                          <span className="count">{unallocatedFuture.length}</span>
                        </div>

                        <div className="is-half-opacity">
                          <UnallocatedDeliveryList
                            unallocatedDeliveries={unallocatedFuture}
                            dashboard={dashboard}
                          />
                        </div>
                      </> : null}
                  </div>
                </div>

                <div className="dispatch-dashboard-section">
                  <div className="dispatch-dashboard-panel">
                    {deliveryGroupList}
                  </div>

                  <div className="dispatch-dashboard-panel">
                    <div className="heading">
                      <span className="text">Drivers</span>
                      <span className="count">{driverCount}</span>
                    </div>

                    {driversAwaitingDeliveries}
                    {driversIdle}
                    {driversBusyFirst}
                    {driversBusySecond}
                    {driversRostered}
                    {driversRosteredToStartSoon}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DragDropProvider>
      </CurrentTimeContext.Provider>
    </DashboardContext.Provider>
  );
}

const DroppableGroupNode = ({
  group,
  groupEl,
  containsDeliveries,
}: {
  group: DeliveryGroup;
  groupEl: ReactNode;
  containsDeliveries: boolean;
}): React.JSX.Element => {
  const { isDropTarget, ref } = useDroppable({
    id: `group:${group.id}`,
    data: {
      id: group.id,
      type: "group"
    }
  });

  const optionalClasses = classNames({
    "has-draggable-hover": isDropTarget,
    "group-placeholder": !containsDeliveries,
  });

  return (
    <div className={`group ${optionalClasses}`} ref={ref}>
      {groupEl}
    </div>
  );
}

interface DeliveryGroupListProps {
  dashboard: Dashboard;
  deliveriesByGroup: Map<number, Delivery[]>;
  apiClient: IDispatchClient;
}

const DeliveryGroupList = ({ dashboard, deliveriesByGroup, apiClient } : DeliveryGroupListProps) => {
  const handleRegroups = useCallback(async () => {
    await apiClient.compactGroups();
  }, [ apiClient ]);

  const handleOrderChronologically = useCallback(async () => {
    await apiClient.orderGroupsChronologically();
  }, [ apiClient ]);

  const allActiveGroupIds = Array.from(deliveriesByGroup.keys());
  const activeDeliveryGroups = dashboard.groups.filter(og => allActiveGroupIds.includes(og.id));

  const totalActiveGroups = activeDeliveryGroups.length;

  const groupBlocks = dashboard.groups.map((group) => {
    const deliveries = deliveriesByGroup.get(group.id) ?? [];
    const groupEl = deliveries.length > 0 ?
      <GroupComponent
        key={group.id}
        dashboard={dashboard}
        group={group}
        deliveries={deliveries}
      /> : <div className="card-placeholder"></div>;

    return (
      <DroppableGroupNode
        group={group}
        groupEl={groupEl}
        containsDeliveries={deliveries.length > 0}
        key={group.id}
      />
    );
  });

  return (
    <>
      <div className="heading is-flex">
        <span className="text">Groups</span>
        <span className="count">{totalActiveGroups}</span>
        <div className="ml-2">
          <button className="button is-small is-text has-tooltip-bottom has-tooltip-arrow" data-tooltip="Tidy up" onClick={() => void handleRegroups()}>
            <span className="icon is-small">
              <i className="fas fa-th" aria-hidden="true"></i>
            </span>
          </button>
          <button className="button is-small is-text has-tooltip-bottom has-tooltip-arrow" data-tooltip="Sort by pickup time" onClick={() => void handleOrderChronologically()}>
            <span className="icon is-small">
              <i className="fas fa-timer" aria-hidden="true"></i>
            </span>
          </button>
        </div>
      </div>
      <div className="group-list">
        {groupBlocks}
      </div>
    </>
  );
}

const StatusBar = ({
 dashboard,
 currentAdminId
} : {
  dashboard: Dashboard;
  currentAdminId: number;
}) => {
  const { apiClient } = useDashboardConfig();
  const currentTime = useContext(CurrentTimeContext);

  const thirtyMinutesOverdue = (delivery: Delivery) => {
    return pickupOverdueBy(delivery, 30) || deliveryOverdueBy(delivery, 30);
  }

  const pickupOverdueBy = (delivery: Delivery, minutes: number) => {
    return !delivery.pickedUpAt && currentTime.getTime() - delivery.pickupTimeIso8601.getTime() > minutes*60*1000;
  }

  const deliveryOverdueBy = (delivery: Delivery, minutes: number) => {
    return currentTime.getTime() - delivery.dropoffTimeIso8601.getTime() > minutes*60*1000;
  }

  const deliveriesNotAcceptedByDriversCount = dashboard.deliveries.filter(delivery => delivery.driverReviewing()).length;
  const driversCanDeliverAgeRestrictedCount = dashboard.driversActive().filter(driver => driver.alcoholDeliveryAllowed).length;
  const deliveriesThirtyMinutesOverdueCount = dashboard.deliveries.filter(delivery => thirtyMinutesOverdue(delivery)).length;


  const takeDashButton = () => {
    if (currentAdminId == dashboard.dispatcherId) {
      return;
    }

    const handleUpdateDispatcher = async () => {
      await apiClient.updateDispatcher();
    };

    return <button className="button tag" onClick={() => void handleUpdateDispatcher()}>
      <span className="icon is-small mr-1">
        <i className="fa-solid fa-share" aria-hidden="true"></i>
      </span>
      Take dash
    </button>;
  }

  const activeDispatcher = () => {
    if (currentAdminId == dashboard.dispatcherId) {
      return;
    }

    if (dashboard.dispatcherId === null) {
      return <span className="tag no-dispatcher-warning">No dispatcher</span>;
    }

    return <span className="tag has-background-black has-text-white">Dispatcher: {dashboard.dispatcherName}</span>;
  }

  const deliveriesNotAcceptedByDrivers = deliveriesNotAcceptedByDriversCount > 0
    ? <span className="tag not-accepted-deliveries-count" title="Number of deliveries that haven't been accepted by the assigned driver">{deliveriesNotAcceptedByDriversCount} {deliveriesNotAcceptedByDriversCount == 1 ? "delivery" : "deliveries"} not accepted</span>
    : <></>;

  const driversCanDeliverAgeRestricted = driversCanDeliverAgeRestrictedCount < 2
    ? <span className="tag" title="Number of drivers that can deliver age restricted goods">{driversCanDeliverAgeRestrictedCount} R18 driver{driversCanDeliverAgeRestrictedCount == 1 ? "" : "s"}</span>
    : <></>;

  const deliveriesThirtyMinutesOverdue = deliveriesThirtyMinutesOverdueCount > 0
    ? <span className="tag thirty-minutes-overdue-count" title="Number of deliveries that are thirty minutes overdue">{deliveriesThirtyMinutesOverdueCount} {deliveriesThirtyMinutesOverdueCount == 1 ? "delivery" : "deliveries"} overdue</span>
    : <></>;


  return (
    <div className="status-bar is-flex is-flex-wrap-wrap is-gap-1.5">
      {takeDashButton()}
      {activeDispatcher()}
      {deliveriesNotAcceptedByDrivers}
      {driversCanDeliverAgeRestricted}
      {deliveriesThirtyMinutesOverdue}
    </div>
  );
}

const UnallocatedDeliveryList = ({
                                   dashboard,
                                   unallocatedDeliveries,
                                 }: {
  dashboard: Dashboard;
  unallocatedDeliveries: Delivery[];
}): React.JSX.Element => {
  const unallocatedDeliveryNodes = unallocatedDeliveries.map(delivery => {
    return (
      <DraggableDeliveryComponent
        defaultExpand={true}
        dashboard={dashboard}
        delivery={delivery}
        key={delivery.id}
        deliveringNow={false}
      />
    );
  })

  return (
    <>
      {unallocatedDeliveryNodes}
    </>
  );
}

const DriverList = ({
                      dashboard,
                      drivers,
                    }: {
  dashboard: Dashboard;
  drivers: Driver[];
}): React.JSX.Element => {
  return (
    <div className="driver-list">
      {drivers.map(driver => {
        return (
          <DriverComponent
            dashboard={dashboard}
            driver={driver}
            key={driver.id}
          />
        );
      })}
    </div>
  );
}

export default DispatchDashboard;
